import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import DragAndDrop from 'components/drag-and-drop/DragAndDrop';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import HelpNote from 'components/help-note/HelpNote';
import UnsentMessagesStorage from './UnsentMessagesStorage';

import { danger } from 'redux-store/reducers/common-reducer';

import { logError } from 'utils/log';
import { SUPPORTED_MIMETYPES } from 'utils/messenger';
import { __t, applyTranslations } from 'utils/language';

import type { PromiseAction, TextEntry } from 'types';

import type { ChangeEvent, KeyboardEvent } from 'react';

applyTranslations(require('./messages-actions.i18n.json'));

type DraftMessage = {
  [x: number]: {
    message: string,
    height: string | undefined | null
  }
};

type OwnProps = {
  profileId: number | undefined | null,
  subcampaignId: number,
  isMessengerDisabled: boolean,
  onAttachFiles: (a: File) => PromiseAction,
  onSendMessage: (a: string) => PromiseAction
};

type Props = {
  dispatch: any
} & OwnProps;

type State = {
  message: string,
  couldNotSendMessage: boolean,
  isSendingMessage: boolean
};

class MessagesActions extends React.PureComponent<Props, State> {
  draftMessages?: DraftMessage;

  input: HTMLTextAreaElement | HTMLInputElement | undefined | null;

  fileInput: HTMLInputElement | undefined | null;

  sentMessageErrorTimer?: number;

  state = {
    message: '',
    couldNotSendMessage: false,
    isSendingMessage: false
  };

  componentDidUpdate(prevProps: Props) {
    const { subcampaignId } = this.props;
    const { message } = this.state;

    if (!isEqual(subcampaignId, prevProps.subcampaignId)) {
      this.draftMessages = {
        ...this.draftMessages,
        [prevProps.subcampaignId]: {
          message,
          height: this.input ? this.input.style.height : null
        }
      };

      const draftMessage = this.draftMessages[subcampaignId];

      this.setState({ // eslint-disable-line react/no-did-update-set-state
        message: draftMessage ? draftMessage.message : ''
      });

      if (this.input) {
        this.input.style.height = draftMessage && draftMessage.height ? String(draftMessage.height) : this.input.style.minHeight;
        this.input.focus();
      }
    }
  }

  componentWillUnmount() {
    if (this.sentMessageErrorTimer) {
      clearTimeout(this.sentMessageErrorTimer);
    }
  }

  async sendAttachments(files: Array<File>) {
    const { onAttachFiles } = this.props;

    if (onAttachFiles) {
      // upload in sequence
      for (let i = 0; i < files.length; i++) {
        await onAttachFiles(files[i]); // eslint-disable-line no-await-in-loop
      }
    }
  }

  handleKeyDown = (event: KeyboardEvent<TextEntry>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      this.handleSendMessage();
    }
  };

  handleInputChange = (event: ChangeEvent<TextEntry>) => {
    this.setState({
      message: event.target.value
    });
  };

  handleAttachFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;

    try {
      await this.sendAttachments([...files ?? []]);
    } catch (error) {
      logError(error, files);
    }

    target.value = '';
  };

  setMessageSendingState = (isSendingMessage: boolean): void => {
    this.setState({
      isSendingMessage
    });
  };

  handleSendMessage = async (): Promise<void> => {
    const { onSendMessage } = this.props;
    const { message } = this.state;
    this.setMessageSendingState(true);

    if (this.sentMessageErrorTimer) {
      clearTimeout(this.sentMessageErrorTimer);
      this.setState({ couldNotSendMessage: false });
    }

    try {
      if (message && message.trim().length > 0 && onSendMessage) {
        await onSendMessage(message);
        this.handleSendMessageSuccess();
      }
    } catch (e) {
      logError(e);
      this.handleSendMessageFailed();
    } finally {
      this.setMessageSendingState(false);
    }
  };

  handleSendMessageSuccess = (): void => {
    if (this.input) {
      this.input.style.height = this.input.style.minHeight;
    }

    this.setState({
      message: ''
    });
  };

  handleSendMessageFailed = (): void => {
    this.setState({ couldNotSendMessage: true });

    this.sentMessageErrorTimer = window.setTimeout(() => {
      this.setState({ couldNotSendMessage: false });
    }, 5000);
  };

  handleShowFileDialog = () => {
    if (this.fileInput) {
      this.fileInput.click();
    }
  };

  handleDrop = (files: Array<File>) => {
    const { dispatch } = this.props;

    const supportedFiles = files.filter(file => SUPPORTED_MIMETYPES.includes(file.type));
    this.sendAttachments(supportedFiles);

    const unsupportedFiles = files.filter(file => !SUPPORTED_MIMETYPES.includes(file.type));
    if (unsupportedFiles.length) {
      const message = __t('messages_actions.unsupported_files', {
        fileList: unsupportedFiles.map(file => `"${file.name}"`).join(', ')
      });
      dispatch(danger(message));
    }
  };

  render() {
    const { isMessengerDisabled, subcampaignId, profileId } = this.props;
    const { message, couldNotSendMessage, isSendingMessage } = this.state;

    return (
      <div className="messenger-actions">
        {
          profileId ?
            <UnsentMessagesStorage
              profileId={profileId}
              campaignId={subcampaignId}
              message={message}
              onUnsentMessage={msg => this.setState({ message: msg })}
            />
            : null
        }
        <DragAndDrop onDrop={this.handleDrop}>
          <Input
            className="messenger-actions__input"
            width="responsive"
            placeholder={!isMessengerDisabled ? __t('messages_actions.messenger_new_message_placeholder') : ''}
            refCallback={(ref) => { this.input = ref; }}
            multiline
            disabled={isMessengerDisabled}
            value={message}
            focusOnMount
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            data={{ qa: 'messenger-actions__input' }}
          />
          <input
            ref={(ref) => { this.fileInput = ref; }}
            className="messenger-actions__file"
            onChange={this.handleAttachFile}
            type="file"
            accept={SUPPORTED_MIMETYPES.join(',')}
            multiple
          />
          <div className="messenger-actions__buttons-root-container">
            <div className="messenger-actions__buttons">
              <ActionButton
                color="tertiary"
                disabled={isMessengerDisabled}
                onClick={this.handleShowFileDialog}
              >
                {__t('messages_actions.send_attachment_button')}
              </ActionButton>
              <div>
                {
                  couldNotSendMessage &&
                    <div className="messenger-actions__error">
                      <SVGDynamicIcon
                        name="exclamation-mark-inside-circle-custom-fg"
                        width={16}
                        height={16}
                        customColor="red"
                      />
                      <span className="messenger-actions__error-label">
                        {__t('messages_actions.send_message_error')}
                      </span>
                    </div>
                }
                <ActionButton
                  color="primary"
                  disabled={isMessengerDisabled || isSendingMessage}
                  onClick={this.handleSendMessage}
                  data={{ qa: 'messenger-actions__button-send' }}
                >
                  {__t('messages_actions.send_message_button')}
                </ActionButton>
              </div>
            </div>
            <HelpNote
              message={__t('messages_actions.upload_text')}
            />
          </div>
        </DragAndDrop>
      </div>
    );
  }
}

export default connect()(MessagesActions);
